import { setUser, unsetUser, setAccounts, setSettings } from "~/state/user/state";
import { store } from "~/state/store";
import { getUserSettings } from "~/util/providers/mapybox";
import { DEFAULT_PREMIUM_DATA, DEFAULT_USER_STATE } from "~/state/user/constants";
import { CONFIG } from "~/config";
import { IUserInfo, IAuthData, ELoginState, IUserBadgeData } from "./state/user/interfaces";
import PreloginDialog, { TPreloginMotivation } from "./ui/dialogs/prelogin-dialog";

const STATE_MAP = {
	[ELoginState.Green]: "login",
	[ELoginState.Orange]: "logout",
	[ELoginState.Red]: "forget",
};

/**
 * Otevreni okna pro registraci
 */
export function registerUser() {
	if ("login" in window) {
		window.login.register.open();
	}
}

/**
 * vyvolavani udalosti badge
 */
function badgeEvent(state: ELoginState) {
	const badge = new CustomEvent("badge", {
		detail: {
			state: STATE_MAP[state],
		},
	});

	window.dispatchEvent(badge);
}

// konfigurace sssp pro souhlas s perosnalizaci bez volani getAds
function ssspConfig() {
	if (window.sssp) {
		window.sssp?.config({
			webId: 36785, // sssp webId mapy.cz/dev/test
			badge: true,
		});
		window.sssp?.init();
		window.sssp?.setPageViewId();
	}
}

/**
 * Trida pro login
 */
export class Login {
	_start: boolean;

	_promise: {
		promise: Promise<any>;
		resolve: (data: any) => void;
		reject: (data?: any) => void;
	};

	_user: IUserInfo;

	_interval: number;

	_impressTimeout: number;

	_visibleTimeout: number;

	_dotConf: {
		[key: string]: any;
	};

	/**
	 * konstruktor
	 */
	constructor() {
		this._start = false;
		this._promise = null;
		this._user = null;
		this._interval = null;
		this._impressTimeout = null;
		this._visibleTimeout = null;
		this._dotConf = {};

		// zavreni okna prihlaseni bez vyplneni udaju a zalogovani
		window.addEventListener("login-close", () => {
			if (this._promise) {
				this._promise.reject();
				this._promise = null;
			}
		});

		// zalogovani z okna loginu
		window.addEventListener("login-done", () => {
			this._sendAuth();
		});
		window.addEventListener("login", () => {
			this._sendAuth();
		});
		window.addEventListener("logout", () => {
			this._sendAuth();
		});
		window.addEventListener("forget", () => {
			this._sendAuth();
		});

		window.addEventListener("badge", (event: CustomEvent<{ state: "login" | "logout" | "forget" }>) => {
			const state = event.detail.state;

			switch (state) {
				// udalost vyvolana autologinem, kdyz jsem green (login) nebo orange (logout)
				case "login":
				case "logout":
					this._sendAuth();
					break;
				// udalost vyvolana autologinem, kdyz nejsem prihlasenej
				case "forget":
				default:
					if (this._impressTimeout) {
						clearTimeout(this._impressTimeout);
					}
					this._setDOTConf();

					this._sendAuth();
			}
		});

		// pokud dojde k navraceni se do tabu ci okna checkneme, zda jsme porad prihlaseni
		document.addEventListener("visibilitychange", () => {
			this._onVisibilityChange();
		}, false);
		window.addEventListener("focus", () => {
			this._onVisibilityChange();
		}, false);
	}

	/**
	 * Rekneme si backendu o stav
	 *
	 * @return {Promise}
	 */
	start() {
		this._start = true;

		if (CONFIG.authData) {
			return this._authDone({
				data: CONFIG.authData,
				status: 200,
			});
		}

		return new Promise(resolve => {
			this._sendAuth().finally(() => resolve({}));
		});
	}

	/**
	 * Otevreni okna prihlaseni
	 */
	open({
		username = "",
		type,
		analyticsData = {},
		motivation = "common",
	}: {
		username?: string;
		type?: any;
		analyticsData?: {
			[key: string]: any;
		},
		motivation?: TPreloginMotivation;
	} = {}): Promise<void> {
		// pokud neni co poslat do analytiky, tak default je preloginScreen a prazdny objekt
		const { loginOrigin = "preloginScreen", ...restAnalytics } = analyticsData;
		let loginType = type;

		if (!type && this._user?.userPublicId && this._user?.accountDisplayName === "") {
			loginType = "relogin";
		}

		let res = null;
		let rej = null;

		const promise = new Promise((resolve, reject) => {
			// napr. pro roboty
			if ("login" in window) {
				if (motivation === "none") {
					Mapy.getComponent("logger").log("login_popup", {
						loginOrigin,
						...restAnalytics,
					});
					window.login.open(loginType ? loginType : null, username);
				} else {
					const preloginDialog = new PreloginDialog({
						motivation,
						onLogin: () => {
							preloginDialog.close();
							Mapy.getComponent("logger").log("login_popup", {
								loginOrigin,
								...restAnalytics,
							});
							window.login.open(loginType ? loginType : null, username);
						},
						onClose: () => {
							preloginDialog.close();
							reject();
						},
					});

					Mapy.getComponent("logger").log(`${loginOrigin}_loginDialogue_show`);
					preloginDialog.open();
				}
			} else {
				reject();
			}

			res = resolve;
			rej = reject;
		});

		this._promise = {
			promise,
			resolve: res,
			reject: rej,
		};

		promise.then(() => {
			Mapy.getComponent("logger").log("login_success", {
				loginOrigin,
				...restAnalytics,
			});
		}, () => {
		});

		return this._promise.promise;
	}

	/**
	 * Odhlaseni uzivatele
	 *
	 * @param  {Boolean} forget zapomenout uzivatele
	 */
	logout(forget?: boolean) {
		return new Promise((resolve, reject) => {
			const opts: any = {
				type: "json",
				method: "POST",
				headers: {
					"Cache-Control": "no-cache, no-store, must-revalidate",
					"Pragma": "no-cache",
					"Expires": "0",
					"accept": "application/json",
				},
			};

			// pokud mame zapomenout, pridame informaci pro backend ve forme parametru
			if (forget) {
				opts.get = {
					forget: "true",
				};
			}

			const request = Mapy.Util.request("/logout", opts);
			const auth = () => this._sendAuth().then(() => {
				resolve({});
			}, () => {
				reject();
			});

			request.promise.finally(() => {
				if (forget && "login" in window) {
					try { // zkusime po zapomenuti zavolat autologin, zda se neprihlasi pod jinym uctem
						window.login.autologin().finally(() => {
							auth();
						});
					} catch (error) {
						auth();
					}
				} else {
					auth();
				}
			});
		});
	}

	/**
	 * prepne aktivniho uzivatele do uzivatele v aktivnim stavu
	 * @param  {Integer} id userId
	 */
	async switchUser(id: number) {
		try {
			await Mapy.Util.request(`${CONFIG.loginUrl}/switch/${id}`, {
				json: {},
				headers: {
					accept: "application/json",
				},
			}).promise;
			await this._sendAuth();

			window.login && login.sync();
		} catch (ex) {
			//
		}
	}

	// pokud dojde z nejakeho backendu, ze je dotaz neautorizovany, zkusime relognout, jinak oznacime uzivatele jako odhlaseneho
	relogin() {
		return this._sendAuth();
	}

	/**
	 * Odeslani dotazu na backend zda je uzivatel prihlaseny
	 *
	 * @return {Promise}
	 */
	_sendAuth() {
		return new Promise((resolve, reject) => {
			const req = Mapy.Util.request(`/auth?${Date.now()}`, {
				type: "json",
				headers: {
					accept: "application/json",
				},
			});

			req.promise.then(data => {
				this._authDone(data as IAuthData).then(() => {
					resolve({});
				}, () => {
					resolve({});
				});
			}, error => {
				this._authDone(error);
				reject();
			});
		});
	}

	_onVisibilityChange() {
		if (!document.hidden && "login" in window && !this._visibleTimeout) {
			try {
				this._visibleTimeout = setTimeout(() => {
					this._visibleTimeout = null;
				}, 200) as unknown as number;
				this._sendAuth();
			} catch (error) {
				//error
			}
		}
	}

	_checkUsers(data?: IAuthData) {
		const auth = data;
		const user = {
			...DEFAULT_USER_STATE.user,
			premiumData: JSON.parse(JSON.stringify(DEFAULT_PREMIUM_DATA)),
		};
		const status = auth ? auth.status : null;

		// pokud mame spravny status, pripravime si objekt pro analytiku
		if (status && status.toString().charAt(0) === "2") {
			const authData = auth.data;

			Object.keys(auth.data).forEach((key: keyof IAuthData["data"]) => {
				switch (key) {
					// vyjimky
					case "userName":
						user.username = authData.userName?.indexOf("@..phone..") > -1
							? ""
							: authData.userName;
						break;

					case "avatarUrl":
						user.avatar = authData.avatarUrl;
						break;

					case "userId":
						user.rusId = authData.userId;
						break;

					case "premiumData":
						user.premiumData = authData.premiumData
							? {
								subscriptions: [...Array.isArray(authData.premiumData.subscriptions) ? authData.premiumData.subscriptions : DEFAULT_PREMIUM_DATA.subscriptions],
								features: {
									/* eslint-disable camelcase */
									advanced_mymaps: authData.premiumData.features?.advanced_mymaps || DEFAULT_PREMIUM_DATA.features.advanced_mymaps,
									advanced_routing: authData.premiumData.features?.advanced_routing || DEFAULT_PREMIUM_DATA.features.advanced_routing,
									offline_map_count: authData.premiumData.features?.offline_map_count || DEFAULT_PREMIUM_DATA.features.offline_map_count,
									premium_support: authData.premiumData.features?.premium_support || DEFAULT_PREMIUM_DATA.features.premium_support,
									user_badge: authData.premiumData.features?.user_badge || DEFAULT_PREMIUM_DATA.features.user_badge,
									watch_support: authData.premiumData.features?.watch_support || DEFAULT_PREMIUM_DATA.features.watch_support,
									watch_support2: authData.premiumData.features?.watch_support2 || DEFAULT_PREMIUM_DATA.features.watch_support2,
									/* eslint-enable camelcase */
								},
								profile: {
									language: authData.premiumData.profile?.language || DEFAULT_PREMIUM_DATA.profile.language,
									country: authData.premiumData.profile?.country || DEFAULT_PREMIUM_DATA.profile.country,
									region: authData.premiumData.profile?.region || DEFAULT_PREMIUM_DATA.profile.region,
									zip: authData.premiumData.profile?.zip || DEFAULT_PREMIUM_DATA.profile.zip,
									offlineMaps: authData.premiumData.profile?.offlineMaps || DEFAULT_PREMIUM_DATA.profile.offlineMaps,
									isPilotUser: authData.premiumData.profile?.isPilotUser || DEFAULT_PREMIUM_DATA.profile.isPilotUser,
									isTrialAvailable: authData.premiumData.profile?.isTrialAvailable || DEFAULT_PREMIUM_DATA.profile.isTrialAvailable,
									id: authData.premiumData.profile?.id || DEFAULT_PREMIUM_DATA.profile.id,
								},
								availableProducts: [...Array.isArray(authData.premiumData.availableProducts) ? authData.premiumData.availableProducts : DEFAULT_PREMIUM_DATA.availableProducts],
							}
							: JSON.parse(JSON.stringify(DEFAULT_PREMIUM_DATA));
						break;

					// stejne prekopirujeme
					default:
						user[key] = authData[key];
				}
			});
		}

		const conf: any = {
			rus: user.accountDisplayName || "",
			rusId: user.rusId || "",
			impress: false,
		};

		// pokud se jedna o prvni odeslani informaci pro analytiku, reknem ji to, pro impress
		const impress = !!this._start || !!this._impressTimeout;

		if (this._start) {
			conf.lazyLoadingEnabled = false;
		}

		// pokud mame uzivatele udelame vse potrebne
		if (user.userPublicId) {
			const change = !(
				this._user
				&& this._user.state === user.state
				&& this._user.userPublicId === user.userPublicId
				&& this._user.premium === user.premium
				&& this._user.sbr === user.sbr
			);

			conf.rus = user.userPublicId;
			conf.rusId = user.rusId;
			conf.loginState = user.state;

			let switchUser = false;

			if (this._user && this._user.rusId !== user.rusId) {
				switchUser = true;
			}

			this._user = user;

			// pokud se jedna o zmenu (pri opakovanem checku prihlaseni timeoutem nebo udalosti visibilitychange)
			if (change) {
				if (user.state === ELoginState.Green) {
					store.dispatch(setUser(user));
					getUserSettings().then(userSettingsData => {
						store.dispatch(setSettings(userSettingsData.items));
					}, () => {
						store.dispatch(setSettings(DEFAULT_USER_STATE.settings));
					}).finally(() => {
						// FIXME: kdo tohle posloucha a je to jeste potreba?
						if (switchUser) {
							window.dispatchEvent(new CustomEvent("login", {}));
						}

						if (this._promise) {
							this._promise.resolve({});
							this._promise = null;
						}
					});
				} else {
					store.dispatch(setSettings(DEFAULT_USER_STATE.settings));

					if (user.state === ELoginState.Orange) {
						store.dispatch(setUser(user));
					} else {
						store.dispatch(unsetUser());
					}

					// FIXME: presunout do profilu
					if (Mapy.getComponent("profile").isActive()) {
						Mapy.getComponent("mymaps").activate();
						Mapy.getComponent("history").save();
					}
				}
			}

			if (this._start) {
				badgeEvent(user.state);
			}

			// interval pro opakovany check zda je uzivatel stale v dannem stavu
			if (this._interval) {
				window.clearInterval(this._interval);
				this._interval = null;
			}

			this._interval = window.setInterval(() => {
				if (!document.hidden) {
					this._sendAuth();
				}
			}, CONFIG.authInterval);
		} else { // uzivatel neni prihlaseny
			// pokud se jedna o prvni pruchod a uzivatel neni prihlaseny zkusime se zeptat jeste autologinu, zda uzivatel neni prihlaseny na jine sluzbe
			if (this._start) {
				if ("login" in window) {
					try {
						window.login.autologin().then(() => {}, () => {});
					} catch (error) {
						//console.log(e);
					}
				}
			// neni prihlaseny, ale driv byl
			} else if (this._user) {
				this._user = null;

				conf.rus = "";
				conf.rusId = "";
				conf.loginState = ELoginState.Red;

				store.dispatch(unsetUser());

				// u odhlaseneho nedelame pravidelny check
				if (this._interval) {
					window.clearInterval(this._interval);
					this._interval = null;
				}

				if ("login" in window) {
					try {
						window.login.autologin().then(() => {}, () => {});
					} catch (error) {
						//console.log(e);
					}
				}
			}
			// FIXME: presunout do profilu
			if (Mapy.getComponent("profile").isActive()) {
				Mapy.getComponent("mymaps").activate(false, true);
				Mapy.getComponent("history").save();
			}
		}

		Object.assign(this._dotConf, conf);

		// pokud jiz byl nasatveny timeout na impress, smazneme jej
		if (this._impressTimeout) {
			clearTimeout(this._impressTimeout);
			this._impressTimeout = null;
		}

		// pokud pri prvnim pruchodu nebyl uzivatel prihlaseny a zkousime autologin, pockame na nej
		if (this._start && !this._user) {
			this._impressTimeout = setTimeout(() => {
				this._setDOTConf(impress);
				this._impressTimeout = null;
			}, 1000) as unknown as number;
		} else { // pokud jiz informace mame, rovnou na analytiku posleme
			this._setDOTConf(impress);
		}

		this._start = false;
	}

	/**
	 * Zpracovani informaci od backendu
	 *
	 * @param  {IAuthData} Informace od backendu
	 */
	_authDone(data?: IAuthData) {
		return Mapy.Util.request(`${CONFIG.loginUrl}/api/v1/user/badge`, {
			get: {
				service: "mapy",
				_: Math.random(),
			},
			type: "json",
			headers: {
				accept: "application/json",
			},
			noABtest: true,
			noXHeaders: true,
		}).promise.then((users: IUserBadgeData) => {
			if (users.data.state !== ELoginState.Red && users.data.users.length > 1) {
				store.dispatch(setAccounts(users.data.users));
			} else {
				store.dispatch(setAccounts([]));
			}

			this._checkUsers(data);
		}, () => {
			store.dispatch(setAccounts([]));
			this._checkUsers(data);
		});
	}

	/**
	 * Odesilani dar do analytiky
	 */
	_setDOTConf(impress?: boolean) {
		if (window.DOT && Object.keys(this._dotConf).length) {
			DOT.cfg(this._dotConf);

			if (impress) {
				DOT.impress(null, {
					lang: CONFIG.defaultLang,
				});
			}

			this._dotConf = {};
		}

		if (impress) {
			ssspConfig();
		}
	}
}
